import type { IAdvertiser, IAdvertiserResponseItem } from '@/Interfaces/Advertiser.interface';
import type { IAgency, IAgencyResponseItem } from '@/Interfaces/Agency.interface';
import type { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import type {
  IClient,
  IClientAdvertiserPayload,
  IClientAgencyPayload,
  IClientGridRow,
  IClientPayload,
  IClientResponseItem,
} from '@/Interfaces/Client.interface';
import type { IClientControlOption, IControlOption } from '@/Interfaces/ControlOption.interface';
import { getControlOptionsValues } from '@/helpers/filters/filterMapping';
import { getSortFilter } from '@/helpers/grid/getSortFilter';
import type { SearchClientsFilters } from '@/types/clients.types';
import { AdvertiserMapper, ribeyeAdvertiserMapper } from '../advertisers/advertiserMappers.helpers';

const agencyMapper = ({ id, name }: IAgency) => ({
  value: id,
  label: name,
});

export const formatClientsResponseData = (
  clients: IClient[],
  advertisersMapper: AdvertiserMapper = ribeyeAdvertiserMapper,
  advertisersFilter?: ((record: IAdvertiser, i?: number, arr?: IAdvertiser[]) => boolean) | null,
  includeEmpty: boolean = false,
) => {
  if (!clients?.length) return [];
  const result: IClientControlOption[] = [];
  clients.forEach(client => {
    const { id, name, advertisers, agencies } = client;

    const processedAdvertisers = advertisers?.length
      ? (advertisersFilter ? advertisers.filter(advertisersFilter) : advertisers).map(advertisersMapper)
      : [];

    if (!processedAdvertisers?.length && !includeEmpty) {
      return;
    }

    const processedAgencies = agencies?.length ? agencies.map(agencyMapper) : [];

    result.push({
      value: id,
      label: name,
      advertisers: processedAdvertisers as IControlOption[],
      agencies: processedAgencies,
    });
  });
  return result.sort((a, b) => a.label.localeCompare(b.label));
};

export const transformClientsFilters = (filters: SearchClientsFilters) => {
  const { whitelabels, sort, ...payload } = filters;

  return {
    ...payload,
    sort: getSortFilter(sort),
    whitelabelIds: getControlOptionsValues(whitelabels),
  };
};

export const formatClientsListResponseData = ({
  data,
  meta,
}: IPaginatedApiResponse<IClientResponseItem[]>): { data: IClientGridRow[]; total: number } => {
  return {
    data: data.map(client => ({
      id: client.id,
      name: client.name,
      externalId: client.external_id ?? '',
      isActive: client.is_active,
      createdAt: client.created_at,
      whitelabel: client.whitelabel
        ? {
            value: client.whitelabel.id,
            label: client.whitelabel.name,
          }
        : null,
      advertisers: client.advertisers?.length
        ? client.advertisers.map(
            advertiser =>
              ({
                value: advertiser.id,
                label: advertiser.name,
              }) as IControlOption,
          )
        : [],
      agencies: client.agencies?.length
        ? client.agencies.map(
            agency =>
              ({
                value: agency.id,
                label: agency.name,
              }) as IControlOption,
          )
        : [],
    })),
    total: meta.total,
  };
};

export const formatControlOptions = ({
  data,
}: IPaginatedApiResponse<IAdvertiserResponseItem[] | IAgencyResponseItem[]>): IControlOption[] => {
  return data.map(advertiser => ({
    value: advertiser.id,
    label: advertiser.name,
  }));
};

export const formatTemporaryControlOption = ({
  name,
  temporaryKey,
}: IClientAgencyPayload | IClientAdvertiserPayload): IControlOption => ({
  value: temporaryKey!,
  label: name,
});

// method reduces temporary control options and duplicated entities
export const prepareClientPayload = (payload: IClientPayload): IClientPayload => {
  const advertiserMap = new Map(payload.newAdvertisers.map(adv => [adv.temporaryKey, adv]));
  const agencyMap = new Map(payload.newAgencies.map(agency => [agency.temporaryKey, agency]));

  const advertisersInAgencies = new Set<number>();

  const newAgencies: IClientAgencyPayload[] = payload.agencyIds
    .map(agencyId => {
      const agency = agencyMap.get(agencyId);
      if (!agency) return null;

      const advertiserMapInAgency = new Map(agency.newAdvertisers.map(adv => [adv.temporaryKey, adv]));

      const newAdvertisersInAgency = agency.advertiserIds
        .map(advertiserId => advertiserMapInAgency.get(advertiserId))
        .filter((advertiser): advertiser is IClientAdvertiserPayload => {
          if (advertiser) {
            advertisersInAgencies.add(advertiser.temporaryKey!);
            return true;
          }
          return false;
        })
        .map(({ temporaryKey, ...rest }) => rest);

      const advertiserIds = agency.advertiserIds.filter(advertiserId => !advertiserMap.has(advertiserId));

      const { temporaryKey, ...cleanedAgency } = agency;

      return {
        ...cleanedAgency,
        advertiserIds,
        newAdvertisers: newAdvertisersInAgency,
      };
    })
    .filter(Boolean) as IClientAgencyPayload[];

  const newAdvertisers: IClientAdvertiserPayload[] = payload.advertiserIds
    .map(advertiserId => advertiserMap.get(advertiserId) as IClientAdvertiserPayload)
    .filter((advertiser): advertiser is IClientAdvertiserPayload => {
      return advertiser && !advertisersInAgencies.has(advertiser.temporaryKey!);
    })
    .map(({ temporaryKey, ...rest }) => rest);

  const advertiserIds = payload.advertiserIds.filter(advertiserId => !advertiserMap.has(advertiserId));
  const agencyIds = payload.agencyIds.filter(agencyId => !agencyMap.has(agencyId));

  return {
    ...payload,
    advertiserIds,
    agencyIds,
    newAdvertisers,
    newAgencies,
  };
};
